const MUSIC_STRUCTURE_TRACK_ID = 'music-structure-track';
const CHORD_TRACK_ID = 'chord-track';
const POSITION_FIELDS = [
  'startMeasure', 'startBeat', 'startSubdivision',
  'endMeasure', 'endBeat', 'endSubdivision'
];

const clone = value => JSON.parse(JSON.stringify(value));

const positionPatchFrom = patch => Object.fromEntries(
  POSITION_FIELDS.filter(field => Object.prototype.hasOwnProperty.call(patch, field))
    .map(field => [field, clone(patch[field])])
);

const defaultIdFactory = () => {
  if (typeof window.crypto?.randomUUID !== 'function') {
    throw new Error('AnnotationBlockStore requires window.crypto.randomUUID');
  }
  return window.crypto.randomUUID();
};

const failedBatch = reason => ({ success: false, reason });

const validationFailure = result => {
  if (result === true || result?.success === true) return null;
  return failedBatch(result?.reason || 'validation-failed');
};

export class AnnotationBlockStore {
  constructor({ idFactory = defaultIdFactory, toUi, toDomain }) {
    this.idFactory = idFactory;
    this.toUi = toUi;
    this.toDomain = toDomain;
    this.blocksById = new Map();
    this.orderedIdsByTrackId = new Map();
    this.trackOrder = [];
  }

  hydrateFromSheet(sheet) {
    this.blocksById = new Map();
    this.orderedIdsByTrackId = new Map();
    this.trackOrder = [];

    this.#hydrateTrack(MUSIC_STRUCTURE_TRACK_ID, sheet.metadata_tracks.music_structure_track);
    this.#hydrateTrack(CHORD_TRACK_ID, sheet.metadata_tracks.chord_track.chords);
    for (const track of sheet.instrument_tracks) {
      this.#hydrateTrack(track.track_id, track.annotation_blocks);
    }
  }

  getAllBlocks() {
    return this.trackOrder.flatMap(trackId => this.getTrackBlocks(trackId));
  }

  getBlock(id) {
    const block = this.blocksById.get(id);
    return block === undefined ? undefined : clone(block);
  }

  getTrackBlocks(trackId) {
    const orderedIds = this.orderedIdsByTrackId.get(trackId) || [];
    return orderedIds.map(id => clone(this.blocksById.get(id)));
  }

  createBlock(block, { resolvePosition } = {}) {
    const trackId = block.trackId;
    if (!this.orderedIdsByTrackId.has(trackId)) {
      throw new Error(`AnnotationBlockStore cannot create a block for unknown track: ${trackId}`);
    }

    let resolvedBlock = clone(block);
    if (resolvePosition) {
      const resolvedPosition = resolvePosition({
        currentBlock: null,
        proposedBlock: clone(resolvedBlock),
        trackBlocks: this.getTrackBlocks(trackId),
      });
      if (!resolvedPosition) return false;
      resolvedBlock = { ...resolvedBlock, ...positionPatchFrom(resolvedPosition) };
    }

    const id = this.#nextUniqueId();
    const localBlock = clone({ ...resolvedBlock, id });
    const nextBlocksById = new Map(this.blocksById);
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextBlocksById.set(id, localBlock);
    nextOrderedIdsByTrackId.set(trackId, [...nextOrderedIdsByTrackId.get(trackId), id]);
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    return clone(localBlock);
  }

  createBlocks(blocks, { validate } = {}) {
    if (!Array.isArray(blocks) || blocks.length === 0) return failedBatch('empty-batch');

    const proposedBlocks = clone(blocks);
    const trackId = proposedBlocks[0]?.trackId;
    if (!this.orderedIdsByTrackId.has(trackId)) return failedBatch('unknown-track');
    if (proposedBlocks.some(block => block.trackId !== trackId)) return failedBatch('mixed-track');

    const trackBlocks = this.getTrackBlocks(trackId);
    if (validate) {
      const failure = validationFailure(validate({
        currentBlocks: [],
        proposedBlocks: clone(proposedBlocks),
        trackBlocks: clone(trackBlocks),
      }));
      if (failure) return failure;
    }

    const reservedIds = new Set(this.blocksById.keys());
    const createdBlocks = proposedBlocks.map(block => {
      const id = this.#nextUniqueId(reservedIds);
      reservedIds.add(id);
      return clone({ ...block, id });
    });
    const nextBlocksById = new Map(this.blocksById);
    createdBlocks.forEach(block => nextBlocksById.set(block.id, clone(block)));
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextOrderedIdsByTrackId.set(trackId, [
      ...nextOrderedIdsByTrackId.get(trackId),
      ...createdBlocks.map(block => block.id),
    ]);
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    return { success: true, blocks: clone(createdBlocks) };
  }

  moveBlocks(changes, { validate } = {}) {
    if (!Array.isArray(changes) || changes.length === 0) return failedBatch('empty-batch');

    const ids = changes.map(change => change.id);
    if (new Set(ids).size !== ids.length) return failedBatch('duplicate-block');
    const currentBlocks = ids.map(id => this.blocksById.get(id));
    if (currentBlocks.some(block => !block)) return failedBatch('missing-block');
    const trackId = currentBlocks[0].trackId;
    if (currentBlocks.some(block => block.trackId !== trackId)) return failedBatch('mixed-track');

    const proposedBlocks = currentBlocks.map((block, index) => ({
      ...clone(block),
      ...positionPatchFrom(changes[index]),
    }));
    const trackBlocks = this.getTrackBlocks(trackId);
    if (validate) {
      const failure = validationFailure(validate({
        currentBlocks: clone(currentBlocks),
        proposedBlocks: clone(proposedBlocks),
        trackBlocks: clone(trackBlocks),
      }));
      if (failure) return failure;
    }

    const nextBlocksById = new Map(this.blocksById);
    proposedBlocks.forEach(block => nextBlocksById.set(block.id, clone(block)));
    this.blocksById = nextBlocksById;
    return { success: true, blocks: clone(proposedBlocks) };
  }

  patchBlockPosition(id, patch, { resolvePosition } = {}) {
    const block = this.blocksById.get(id);
    if (!block) return false;

    let positionPatch = positionPatchFrom(patch);
    if (resolvePosition) {
      const resolvedPosition = resolvePosition({
        currentBlock: clone(block),
        proposedBlock: clone({ ...block, ...positionPatch }),
        trackBlocks: this.getTrackBlocks(block.trackId),
      });
      if (!resolvedPosition) return false;
      positionPatch = positionPatchFrom(resolvedPosition);
    }

    const nextBlocksById = new Map(this.blocksById);
    nextBlocksById.set(id, { ...block, ...positionPatch });
    this.blocksById = nextBlocksById;
    return true;
  }

  patchBlockFormData(id, patch) {
    const block = this.blocksById.get(id);
    if (!block) return false;

    const nextBlocksById = new Map(this.blocksById);
    nextBlocksById.set(id, {
      ...block,
      formData: { ...clone(block.formData || {}), ...clone(patch) }
    });
    this.blocksById = nextBlocksById;
    return true;
  }

  deleteBlock(id) {
    const block = this.blocksById.get(id);
    if (!block) return false;

    const nextBlocksById = new Map(this.blocksById);
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextBlocksById.delete(id);
    nextOrderedIdsByTrackId.set(
      block.trackId,
      nextOrderedIdsByTrackId.get(block.trackId).filter(orderedId => orderedId !== id)
    );
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    return true;
  }

  deleteBlocks(ids) {
    if (!Array.isArray(ids) || ids.length === 0) return failedBatch('empty-batch');
    if (new Set(ids).size !== ids.length) return failedBatch('duplicate-block');

    const blocks = ids.map(id => this.blocksById.get(id));
    if (blocks.some(block => !block)) return failedBatch('missing-block');
    const trackId = blocks[0].trackId;
    if (blocks.some(block => block.trackId !== trackId)) return failedBatch('mixed-track');

    const deletedIds = new Set(ids);
    const nextBlocksById = new Map(this.blocksById);
    ids.forEach(id => nextBlocksById.delete(id));
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextOrderedIdsByTrackId.set(
      trackId,
      nextOrderedIdsByTrackId.get(trackId).filter(id => !deletedIds.has(id))
    );
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    return { success: true, blocks: clone(blocks) };
  }

  addTrack(trackId) {
    if (this.orderedIdsByTrackId.has(trackId)) return false;
    this.orderedIdsByTrackId = new Map(this.orderedIdsByTrackId).set(trackId, []);
    this.trackOrder = [...this.trackOrder, trackId];
    return true;
  }

  removeTrack(trackId) {
    const orderedIds = this.orderedIdsByTrackId.get(trackId);
    if (!orderedIds) return false;

    const nextBlocksById = new Map(this.blocksById);
    orderedIds.forEach(id => nextBlocksById.delete(id));
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextOrderedIdsByTrackId.delete(trackId);
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    this.trackOrder = this.trackOrder.filter(id => id !== trackId);
    return true;
  }

  renameTrack(trackId, newTrackId) {
    const orderedIds = this.orderedIdsByTrackId.get(trackId);
    if (!orderedIds || (trackId !== newTrackId && this.orderedIdsByTrackId.has(newTrackId))) return false;
    if (trackId === newTrackId) return true;

    const nextBlocksById = new Map(this.blocksById);
    orderedIds.forEach(id => {
      nextBlocksById.set(id, { ...nextBlocksById.get(id), trackId: newTrackId });
    });
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextOrderedIdsByTrackId.delete(trackId);
    nextOrderedIdsByTrackId.set(newTrackId, [...orderedIds]);
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    this.trackOrder = this.trackOrder.map(id => id === trackId ? newTrackId : id);
    return true;
  }

  clearAndRenameTrack(trackId, newTrackId) {
    const orderedIds = this.orderedIdsByTrackId.get(trackId);
    if (!orderedIds || (trackId !== newTrackId && this.orderedIdsByTrackId.has(newTrackId))) return false;

    const nextBlocksById = new Map(this.blocksById);
    orderedIds.forEach(id => nextBlocksById.delete(id));
    const nextOrderedIdsByTrackId = new Map(this.orderedIdsByTrackId);
    nextOrderedIdsByTrackId.delete(trackId);
    nextOrderedIdsByTrackId.set(newTrackId, []);
    this.blocksById = nextBlocksById;
    this.orderedIdsByTrackId = nextOrderedIdsByTrackId;
    this.trackOrder = this.trackOrder.map(id => id === trackId ? newTrackId : id);
    return true;
  }

  transformTrack(trackId, transform) {
    const orderedIds = this.orderedIdsByTrackId.get(trackId);
    if (!orderedIds) return false;

    const nextBlocksById = new Map(this.blocksById);
    orderedIds.forEach(id => {
      const current = clone(nextBlocksById.get(id));
      const transformed = transform(current);
      const nextBlock = transformed === undefined ? current : clone(transformed);
      nextBlocksById.set(id, { ...nextBlock, id, trackId });
    });
    this.blocksById = nextBlocksById;
    return true;
  }

  projectOntoSheet(sheet) {
    const sheetInstrumentTracks = new Map();
    for (const track of sheet.instrument_tracks) {
      if (sheetInstrumentTracks.has(track.track_id)) {
        throw new Error(`AnnotationBlockStore found duplicate sheet track: ${track.track_id}`);
      }
      sheetInstrumentTracks.set(track.track_id, track);
    }

    const expectedTrackIds = [
      MUSIC_STRUCTURE_TRACK_ID,
      CHORD_TRACK_ID,
      ...sheet.instrument_tracks.map(track => track.track_id)
    ];
    if (this.trackOrder.length !== expectedTrackIds.length ||
        this.trackOrder.some((trackId, index) => trackId !== expectedTrackIds[index])) {
      const unknownTrack = this.trackOrder.find(trackId => !expectedTrackIds.includes(trackId));
      if (unknownTrack) {
        throw new Error(`AnnotationBlockStore track ${unknownTrack} does not exist on the sheet`);
      }
      throw new Error('AnnotationBlockStore track order does not match the sheet');
    }

    const seenIds = new Set();
    const projectedByTrackId = new Map();
    for (const trackId of this.trackOrder) {
      const orderedIds = this.orderedIdsByTrackId.get(trackId);
      if (!orderedIds) {
        throw new Error(`AnnotationBlockStore has no ordered IDs for track: ${trackId}`);
      }
      const projectedBlocks = orderedIds.map(id => {
        if (seenIds.has(id)) {
          throw new Error(`AnnotationBlockStore found duplicate block ID reference: ${id}`);
        }
        seenIds.add(id);
        const block = this.blocksById.get(id);
        if (!block) {
          throw new Error(`AnnotationBlockStore found missing ordered block ID: ${id}`);
        }
        if (block.trackId !== trackId) {
          throw new Error(`AnnotationBlockStore block ${id} does not belong to track ${trackId}`);
        }
        const projected = clone(this.toDomain(clone(block)));
        delete projected.id;
        delete projected.clientId;
        delete projected.sessionId;
        delete projected.trackId;
        delete projected.colorType;
        delete projected.fullInstrumentPath;
        return projected;
      });
      projectedByTrackId.set(trackId, projectedBlocks);
    }
    if (seenIds.size !== this.blocksById.size) {
      throw new Error('AnnotationBlockStore contains a block without an ordered track reference');
    }

    const projection = {
      musicStructure: projectedByTrackId.get(MUSIC_STRUCTURE_TRACK_ID),
      chords: projectedByTrackId.get(CHORD_TRACK_ID),
      instrumentByTrackId: new Map(
        sheet.instrument_tracks.map(track => [track.track_id, projectedByTrackId.get(track.track_id)])
      )
    };

    sheet.metadata_tracks.music_structure_track = projection.musicStructure;
    sheet.metadata_tracks.chord_track.chords = projection.chords;
    for (const track of sheet.instrument_tracks) {
      track.annotation_blocks = projection.instrumentByTrackId.get(track.track_id);
    }
  }

  #hydrateTrack(trackId, domainBlocks) {
    if (this.orderedIdsByTrackId.has(trackId)) {
      throw new Error(`AnnotationBlockStore found duplicate track ID during hydration: ${trackId}`);
    }
    const orderedIds = [];
    this.trackOrder.push(trackId);
    this.orderedIdsByTrackId.set(trackId, orderedIds);

    for (const domainBlock of domainBlocks) {
      const id = this.#nextUniqueId();
      const localBlock = clone({
        ...this.toUi(clone(domainBlock), trackId, id),
        id,
        trackId
      });
      this.blocksById.set(id, localBlock);
      orderedIds.push(id);
    }
  }

  #nextUniqueId(reservedIds = this.blocksById) {
    const id = this.idFactory();
    if (reservedIds.has(id)) {
      throw new Error(`AnnotationBlockStore generated duplicate ID: ${id}`);
    }
    return id;
  }
}
